import { css } from 'styled-components';
import THEME, { COLOR } from '../../styles/colors';
import Font, { FONT_CONFIG } from '../../styles/font';
import {
  IColumnStyles,
  IStyledTableProps,
  ITableConfig,
  TRowStylesConfig
} from './TableTypes';

const PADDING = 20;

/* Standard table padding */
export const cellPadding = css`
  padding: ${PADDING}px;
`;

/* Reduce padding by 1/2 in between cells */
export const smartCellPadding = css<{ $config?: ITableConfig }>`
  padding: ${PADDING}px ${PADDING / 2}px;
  &:first-child {
    padding-left: ${props => (props.$config?.table?.isPadded ? 0 : '20px')};
  }
  &:last-child {
    padding-right: ${props => (props.$config?.table?.isPadded ? 0 : '20px')};
  }
`;

const parseColumnConfig = (cols?: (string | undefined)[]): string => {
  if (!cols) return '';
  const cssRules = cols.map((c, i) =>
    c ? `&:nth-child(${i + 1}) { ${c} };` : ''
  );
  return cssRules.join('\n');
};

const parseRowConfig = (rows?: TRowStylesConfig): string => {
  if (!rows) return '';
  const cssRules = Object.entries(rows).map(([i, r]) => {
    if (!r) return '';
    if (i === 'all') return `& { ${r} };`;
    if (i === 'first') return `&:first-child { ${r} };`;
    if (i === 'last') return `&:last-child { ${r} };`;
    return `&:nth-child(${i}) { ${r} };`;
  });
  return cssRules.join('\n');
};

enum TableSection {
  Head,
  Body,
  Foot
}

const parseColumnStyles = (
  columnStyles?: IColumnStyles,
  tableSection?: TableSection
): string => {
  if (!columnStyles) return '';

  const styles: string[] = [];

  if (columnStyles?.all) styles.push(columnStyles?.all);
  if (columnStyles?.allByIndex)
    styles.push(parseColumnConfig(columnStyles?.allByIndex));

  if (tableSection === TableSection.Head) {
    if (columnStyles?.allHeaders) styles.push(columnStyles?.allHeaders);
    if (columnStyles?.header)
      styles.push(parseColumnConfig(columnStyles?.header));
  } else if (tableSection === TableSection.Body) {
    if (columnStyles?.allBody) styles.push(columnStyles?.allBody);
    if (columnStyles?.body) styles.push(parseColumnConfig(columnStyles?.body));
  } else if (tableSection === TableSection.Foot) {
    if (columnStyles?.allFooters) styles.push(columnStyles?.allFooters);
    if (columnStyles?.footer)
      styles.push(parseColumnConfig(columnStyles?.footer));
  }

  return styles.join('\n');
};

export const printStyles = css`
  width: 100%;
  white-space: nowrap;
  text-align: left;
  vertical-align: middle;

  & td,
  & th {
    padding: 6pt;
    page-break-inside: avoid;
  }

  & thead td,
  & thead th {
    font-weight: 700;
  }

  & tr {
    page-break-inside: avoid;
    border-bottom: 1px solid ${THEME.general.border};
  }

  & > tbody > tr:nth-child(2n - 1) {
    background-color: ${COLOR.neutral0};
  }
`;

export const table1Styles = css<IStyledTableProps>`
  width: 100%;
  white-space: nowrap;
  text-align: center;
  padding: 0 20px;

  &.alternate-rows > tbody > tr:nth-child(2n + 1) {
    background-color: ${THEME.surface.tertiary};
  }

  & > tbody {
    & > tr {
      width: 100%;
      background-color: ${THEME.surface.primary};

      ${FONT_CONFIG.get(Font.f14_tall)?.css}

      ${props => parseRowConfig(props.$config?.rowStyles?.body)}

      &:not(:last-child) {
        border-bottom: 1px solid ${THEME.general.border};
      }

      /* needs updated to use a theme provider */
      &.highlight {
        background-color: ${THEME.surface.tertiary};
      }
      &.highlight-deleted {
        background-color: ${THEME.other.deleted};
      }

      & > td,
      & > th {
        ${smartCellPadding}
        vertical-align: middle;

        ${/* eslint-disable-next-line prettier/prettier */ ''}
        ${props => parseColumnStyles(props.$config?.columnStyles, TableSection.Body)}}
    }
    & > tr.contextual {
      font-weight: bold;
    }
  }

  & > thead {
    & > tr {
      color: ${THEME.text.tertiary};
      border-bottom: 1px solid ${THEME.general.border};

      ${FONT_CONFIG.get(Font.f14_bold_tall)?.css}

      ${props => parseRowConfig(props.$config?.rowStyles?.header)}

      & > td,
      & > th {
        ${smartCellPadding}
        padding-top: 8px;
        padding-bottom: 8px;

        vertical-align: middle;

        ${/* eslint-disable-next-line prettier/prettier */ ''}
        ${props => parseColumnStyles(props.$config?.columnStyles, TableSection.Head)}}
      }
    }
  }

  & > tfoot {
    & > tr {
      ${props => parseRowConfig(props.$config?.rowStyles?.footer)}

      & > td,
      & > th {
        vertical-align: middle;

        ${/* eslint-disable-next-line prettier/prettier */ ''}
        ${props => parseColumnStyles(props.$config?.columnStyles, TableSection.Foot)}}
      }
    }
  }
`;
